import { Injectable, signal, computed } from '@angular/core';

/**
 * Service to play notification sounds when WebSocket notifications arrive.
 * Supports user preference to enable/disable sounds.
 */
@Injectable({ providedIn: 'root' })
export class NotificationSoundService {
  private readonly _enabled = signal<boolean>(true);
  private audioContext: AudioContext | null = null;
  private notificationSound: AudioBuffer | null = null;

  readonly enabled = computed(() => this._enabled());

  constructor() {
    // Load user preference from localStorage
    const savedPreference = localStorage.getItem('mis-notification-sound-enabled');
    if (savedPreference !== null) {
      this._enabled.set(savedPreference === 'true');
    }

    // Initialize Web Audio API (lazy initialization)
    this.initializeAudio();
  }

  /**
   * Initialize Web Audio API and create notification sound
   */
  private async initializeAudio(): Promise<void> {
    try {
      // Check if Web Audio API is supported
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.warn('Web Audio API not supported, notification sounds disabled');
        return;
      }

      // Create audio context
      this.audioContext = new AudioContextClass();
      
      // Firefox requires the context to be resumed after user interaction
      // We'll resume it when play() is called after user interaction
      
      // Create a pleasant notification sound using Web Audio API
      this.createNotificationSound();
      
      if (this.audioContext) {
        console.log('✅ Audio context initialized:', this.audioContext.state);
      }
    } catch (error) {
      console.warn('Failed to initialize audio context:', error);
    }
  }

  /**
   * Create a pleasant notification sound using Web Audio API
   * This creates a short, pleasant "ding" sound
   */
  private createNotificationSound(): void {
    if (!this.audioContext) return;

    try {
      // Create a short notification sound: two-tone chime
      const sampleRate = this.audioContext.sampleRate;
      const duration = 0.3; // 300ms
      const buffer = this.audioContext.createBuffer(1, sampleRate * duration, sampleRate);
      const data = buffer.getChannelData(0);

      // Generate a pleasant two-tone chime
      for (let i = 0; i < buffer.length; i++) {
        const t = i / sampleRate;
        // First tone (higher frequency)
        const freq1 = 800; // Hz
        const freq2 = 1000; // Hz
        // Envelope to make it fade out smoothly
        const envelope = Math.exp(-t * 5); // Exponential decay
        // Mix two frequencies with envelope
        data[i] = envelope * (
          Math.sin(2 * Math.PI * freq1 * t) * 0.5 +
          Math.sin(2 * Math.PI * freq2 * t) * 0.5
        );
      }

      this.notificationSound = buffer;
    } catch (error) {
      console.warn('Failed to create notification sound:', error);
    }
  }

  /**
   * Play the notification sound
   */
  async play(): Promise<void> {
    if (!this._enabled()) {
      console.log('🔇 Sound disabled by user');
      return; // Sound disabled by user
    }

    if (!this.audioContext || !this.notificationSound) {
      // Fallback: try to initialize audio if not already done
      await this.initializeAudio();
      if (!this.audioContext || !this.notificationSound) {
        console.warn('⚠️ Audio context or sound buffer not available');
        return; // Still can't play sound
      }
    }

    try {
      // Firefox requires explicit resume after user interaction
      // Resume audio context if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        console.log('🔄 Resuming suspended audio context...');
        await this.audioContext.resume();
        console.log('✅ Audio context resumed:', this.audioContext.state);
      }

      // Ensure context is running
      if (this.audioContext.state !== 'running') {
        console.warn('⚠️ Audio context not running, state:', this.audioContext.state);
        // Try to resume again
        await this.audioContext.resume();
      }

      // Create a new source for each play (allows overlapping sounds)
      const source = this.audioContext.createBufferSource();
      source.buffer = this.notificationSound;
      
      // Add gain node for volume control
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 0.5; // 50% volume to avoid being too loud
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);
      console.log('🔊 Notification sound played');
      
      // Clean up after sound finishes
      source.onended = () => {
        source.disconnect();
        gainNode.disconnect();
      };
    } catch (error) {
      console.error('❌ Failed to play notification sound:', error);
      // Fallback: try using HTML5 Audio API
      this.playFallbackSound();
    }
  }

  /**
   * Fallback: Use HTML5 Audio API with oscillator (simpler approach for Firefox)
   */
  private async playFallbackSound(): Promise<void> {
    try {
      console.log('🔄 Trying fallback sound method...');
      // Create a simple beep using Audio API
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.warn('⚠️ AudioContext not available for fallback');
        return;
      }

      const ctx = new AudioContextClass();
      
      // Resume if suspended
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
      
      console.log('✅ Fallback sound played');
    } catch (error) {
      console.error('❌ Fallback sound also failed:', error);
    }
  }

  /**
   * Enable notification sounds
   */
  async enable(): Promise<void> {
    this._enabled.set(true);
    localStorage.setItem('mis-notification-sound-enabled', 'true');
    // Test sound when enabling (this also unlocks audio for Firefox)
    await this.play();
  }

  /**
   * Disable notification sounds
   */
  disable(): void {
    this._enabled.set(false);
    localStorage.setItem('mis-notification-sound-enabled', 'false');
  }

  /**
   * Toggle notification sounds on/off
   */
  async toggle(): Promise<void> {
    if (this._enabled()) {
      this.disable();
    } else {
      await this.enable();
    }
  }

  /**
   * Test the notification sound (used to unlock audio after user interaction)
   */
  async test(): Promise<void> {
    console.log('🧪 Testing notification sound...');
    await this.play();
  }

  /**
   * Unlock audio context (call this after user interaction for Firefox compatibility)
   */
  async unlockAudio(): Promise<void> {
    if (!this.audioContext) {
      await this.initializeAudio();
    }
    
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('✅ Audio unlocked, state:', this.audioContext.state);
      } catch (error) {
        console.warn('⚠️ Failed to unlock audio:', error);
      }
    }
  }
}

